import { CardCarousel } from './CardCarousel';
import { type PersonalityConfig } from '../api/gameConfig';
import { formatModifierValue } from '../utils/modifierLabels';
import { COLORS } from '../constants/theme';

const PERSONALITY_GRADIENTS: Record<string, string> = {
  MERCHANT:  'linear-gradient(160deg, #241a08 0%, #382810 100%)',
  SHEPHERD:  'linear-gradient(160deg, #162018 0%, #1e3020 100%)',
  SAGE:      'linear-gradient(160deg, #101828 0%, #162438 100%)',
  ROGUE:     'linear-gradient(160deg, #141414 0%, #201818 100%)',
  MYSTIC:    'linear-gradient(160deg, #1a0e24 0%, #2a1432 100%)',
  WARRIOR:   'linear-gradient(160deg, #280e0e 0%, #3c1010 100%)',
  CLERIC:    'linear-gradient(160deg, #181824 0%, #222438 100%)',
  WAR_HERO:  'linear-gradient(160deg, #201808 0%, #342810 100%)',
  GENERAL:   'linear-gradient(160deg, #0e1c10 0%, #162818 100%)',
  TACTICIAN: 'linear-gradient(160deg, #141c20 0%, #1e2c30 100%)',
};

interface ManualModifier {
  label: string;
  display: string;
  sign: 'positive' | 'negative' | 'neutral';
}

// Mechanics not exposed through the API modifiers map — rendered as table rows.
// Remove entries here once the backend exposes them via PersonalityConfigDto.modifiers.
const PERSONALITY_EXTRA_MODIFIERS: Partial<Record<string, ManualModifier[]>> = {
  MERCHANT: [
    { label: 'Income',          display: '+30%',  sign: 'positive' },
  ],
  SHEPHERD: [
    { label: 'Birth rate',      display: '+25%',  sign: 'positive' },
  ],
  SAGE: [
    { label: 'Science output',  display: '+25%',  sign: 'positive' },
  ],
  ROGUE: [
    { label: 'Stealth recovery', display: '+1/day', sign: 'positive' },
    { label: 'Operations',       display: 'All',    sign: 'positive' },
  ],
  MYSTIC: [
    { label: 'Guilds & towers', display: '2×',    sign: 'positive' },
  ],
  CLERIC: [
    { label: 'Own casualties',  display: '−50%',  sign: 'positive' },
    { label: 'Animate Dead',    display: 'Active', sign: 'positive' },
  ],
};

interface PersonalitySelectorProps {
  personalities: PersonalityConfig[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function PersonalityCard({
  personality,
  isSelected,
}: {
  personality: PersonalityConfig;
  isSelected: boolean;
}) {
  const apiModifiers = Object.entries(personality.modifiers)
    .map(([key, value]) => formatModifierValue(key, value))
    .filter((m): m is NonNullable<typeof m> => m !== null);

  const modifiers = [...apiModifiers, ...(PERSONALITY_EXTRA_MODIFIERS[personality.id] ?? [])];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Image / gradient */}
      <div style={{
        height: 72,
        background: PERSONALITY_GRADIENTS[personality.id] ?? PERSONALITY_GRADIENTS.MERCHANT,
        borderRadius: '6px 6px 0 0',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        <img
          src={`/images/personalities/${personality.id.toLowerCase()}.webp`}
          alt={`${personality.name}`}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7, flex: 1 }}>
        {/* Name + selected badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: isSelected ? COLORS.link : COLORS.text.primary, flex: 1 }}>
            {personality.name}
          </div>
          {isSelected && (
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 3,
              backgroundColor: COLORS.link, color: COLORS.bg, letterSpacing: '0.05em',
              textTransform: 'uppercase', flexShrink: 0,
            }}>
              ✓
            </span>
          )}
        </div>

        {/* Modifiers */}
        {modifiers.length > 0 && (
          <div>
            <div style={{ fontSize: 9, fontWeight: 600, color: COLORS.text.tertiary, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              Modifiers
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {modifiers.map((m) => (
                  <tr key={m.label}>
                    <td style={{ color: COLORS.text.secondary, paddingTop: 1 }}>{m.label}</td>
                    <td style={{
                      textAlign: 'right',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: m.sign === 'positive' ? COLORS.positive : m.sign === 'negative' ? COLORS.negative : COLORS.text.primary,
                    }}>
                      {m.display}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function PersonalitySelector({
  personalities,
  selectedId,
  onSelect,
}: PersonalitySelectorProps) {
  return (
    <CardCarousel
      items={personalities}
      selectedId={selectedId}
      onSelect={onSelect}
      getItemId={(p) => p.id}
      ariaLabel="Personality"
      renderCard={(personality, isSelected) => (
        <PersonalityCard personality={personality} isSelected={isSelected} />
      )}
    />
  );
}
